import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { JOB_CATEGORIES, roleToSlug } from '../utils/constants';
import { JOB_DETAILS } from '../utils/jobDetails';

const JobDetail = () => {
  const { role } = useParams();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    visaSponsor: 'no',
    cv: null,
    photo: null
  });

  // 🔹 Find role name from categories
  let roleName = null;
  for (const category of JOB_CATEGORIES) {
    const foundRole = category.roles.find(r => roleToSlug(r) === role);
    if (foundRole) {
      roleName = foundRole;
      break;
    }
  }

  // If role slug invalid → go back
  if (!roleName) {
    return <Navigate to="/careers" replace />;
  }

  // 🔹 Fetch role-specific details
  const roleData = JOB_DETAILS[role];

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'file' ? files[0] : value
    });
  };

  return (
    <div className="min-h-screen bg-black text-white py-16">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Job Title */}
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {roleName}
          </h1>

          {/* Short Summary */}
          <p className="text-white/60 mb-12">
            {roleData?.summary || 'Explore this role at Neufora and be part of building future-ready technology.'}
          </p>

          {/* Job Sections */}
          <div className="space-y-10 mb-12">
            {/* Description */}
            <div>
              <h2 className="text-2xl font-semibold mb-3">Description</h2>
              <p className="text-white/70 leading-relaxed">
                {roleData?.description || 'Detailed role description will be updated soon.'}
              </p>
            </div>

            {/* Qualifications */}
            <div>
              <h2 className="text-2xl font-semibold mb-3">Qualifications</h2>
              <ul className="text-white/70 space-y-2 leading-relaxed">
                {(roleData?.qualifications || []).map((q, i) => (
                  <li key={i}>• {q}</li>
                ))}
              </ul>
            </div>

            {/* Additional Qualifications */}
            <div>
              <h2 className="text-2xl font-semibold mb-3">Additional Qualifications</h2>
              <ul className="text-white/70 space-y-2 leading-relaxed">
                {(roleData?.additionalQualifications || []).map((q, i) => (
                  <li key={i}>• {q}</li>
                ))}
              </ul>
            </div>

            {/* Salary */}
            <div>
              <h2 className="text-2xl font-semibold mb-3">Expected Salary</h2>
              <p className="text-white/70">
                {roleData?.salary || 'Competitive compensation based on experience'}
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-semibold mb-3">working hours</h2>
              <p className="text-white/70">
                {roleData?.workinghours || 'flexible working hours'}
              </p>
            </div>

            {/* Location */}
            <div>
              <h2 className="text-2xl font-semibold mb-3">Job Location</h2>
              <p className="text-white/70">
                {roleData?.location || 'UK'}
              </p>
            </div>
          </div>

          {/* Notice */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-10">
            <p className="text-white/80">
              Additional strict technical and domain-specific questions may be asked during interviews.
            </p>
          </div>

          {/* Application Form */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
            <h2 className="text-3xl font-bold mb-8 text-center">
              Apply Now
            </h2>

            <form
              action="https://formsubmit.co/contact.neufora+v2@gmail.com"
              method="POST"
              encType="multipart/form-data"
              className="space-y-6"
            >
               <input
  type="hidden"
  name="_next"
  value="http://neufora.com/thank-you"
/>

              {/* FormSubmit Config */}
              <input type="hidden" name="_captcha" value="false" />
              <input
                type="hidden"
                name="_subject"
                value={`Job Application: ${roleName}`}
              />
              <input type="hidden" name="role" value={roleName} />

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-white/30 focus:outline-none transition-colors"
                />
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-white/30 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+44 XXXXX XXXXX"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-white/30 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  required
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Your country"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-white/30 focus:outline-none transition-colors"
                />
              </div>

              {/* Visa Sponsorship */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-3">
                  Visa sponsorship needed?
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="visaSponsor"
                      value="yes"
                      checked={formData.visaSponsor === "yes"}
                      onChange={handleChange}
                    />
                    Yes
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="visaSponsor"
                      value="no"
                      checked={formData.visaSponsor === "no"}
                      onChange={handleChange}
                    />
                    No
                  </label>
                </div>
              </div>

              {/* CV Upload */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Upload CV
                </label>
                <input
                  type="file"
                  name="cv"
                  required
                  accept=".pdf,.doc,.docx"
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-white/10 file:text-white hover:file:bg-white/20 transition"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Upload Photo
                </label>
                <input
                  type="file"
                  name="photo"
                  required
                  accept="image/*"
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-white/10 file:text-white hover:file:bg-white/20 transition"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="w-full px-8 py-4 bg-white text-black rounded-xl font-semibold hover:scale-[1.02] transition-transform"
              >
                Submit Application
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default JobDetail;